import axios from 'axios';

import {
  IEmployeeCredentialsUpdateDto,
  IEmployeeDto,
  IEmployeeJobPosition,
  IEmployeeProfile,
  IEmployeeProfileDto,
  IEmployeeRole,
} from 'common/interfaces/employee';
import { IPaginatedEmployees } from 'modules/employees/interfaces';
import { ITableParams } from 'common/interfaces/table';
import ApiService from './ApiService';
import { ISuccessResponse } from 'common/interfaces/http';
import { IPeakEmployeeFormData, IPeakEmployeeProfile } from 'modules/peakEmployees/interfaces';
import { IAttachment } from 'common/interfaces/uploadFile';

export default class EmployeesService extends ApiService {
  public getPeakEmployees = (requestOptions?: ITableParams): Promise<IPaginatedEmployees> => {
    const searchParams = this.getSearchFromTableParams(requestOptions);
    return axios.get('/api/v1/peak-employees', { params: searchParams });
  };

  public getProfile = (id: number): Promise<IEmployeeDto> => {
    return axios.get(`/api/v1/peak-employees/${id}/profile`);
  };

  public getProfileView = (id: number): Promise<IEmployeeProfileDto> => {
    return axios.get(`/api/v1/peak-employees/${id}/profile/view`);
  };

  public updateProfile = (requestData: IEmployeeDto): Promise<IEmployeeProfileDto> => {
    return axios.put(`/api/v1/peak-employees/${requestData.id}/profile`, requestData);
  };

  public getEmployeePermissions = (id: number): Promise<IEmployeeRole> => {
    return axios.get(`/api/v1/peak-employees/${id}/permissions`);
  };

  public updateEmployeePermissions = (id: number, role: IEmployeeRole): Promise<void> => {
    return axios.put(`/api/v1/peak-employees/${id}/permissions`, role);
  };

  public getEmployeeAttachments = (id: number): Promise<IAttachment[]> => {
    return axios.get(`/api/v1/peak-employees/${id}/attachments`);
  };

  public updateEmployeeAttachments = (id: number, attachments: IAttachment[]): Promise<void> => {
    return axios.put(`/api/v1/peak-employees/${id}/attachments`, attachments);
  };

  public getEmployeeJobPositions = (id: number): Promise<IEmployeeJobPosition[]> => {
    return axios.get(`/api/v1/peak-employees/${id}/job-positions`);
  };

  public getEmployeeJobPositionsHistory = (id: number): Promise<IEmployeeJobPosition[]> => {
    return axios.get(`/api/v1/peak-employees/${id}/job-positions`);
  };

  public updateEmployeeJobPositions = (
    id: number,
    data: { jobPositions: IEmployeeJobPosition[] },
  ): Promise<void> => {
    return axios.put(`/api/v1/peak-employees/${id}/job-positions`, data);
  };

  public updateEmployeeCredentials = (
    id: number,
    credentials: IEmployeeCredentialsUpdateDto,
  ): Promise<void> => {
    return axios.put(`/api/v1/peak-employees/${id}/credentials`, credentials);
  };

  public deletePhoto = (id: number): Promise<void> => {
    return axios.delete(`/api/v1/peak-employees/${id}/avatar`);
  };

  public uploadPhoto = async (id: number, image: IAttachment): Promise<void> => {
    await axios.put(`/api/v1/peak-employees/${id}/avatar`, image);
  };

  public deleteEmployeeAttachment = (id: number, attachmentId: string): Promise<void> => {
    return axios.delete(`/api/v1/peak-employees/${id}/attachments/${attachmentId}`);
  };

  // public getEmployeesListWithSearch = (search: {
  //   searchStr: string;
  // }): Promise<IPaginatedEmployees> => {
  //   return axios.get('/api/v1/employees', { params: { searchStr } });
  // };

  public getPeakEmployeeById = (id: number): Promise<IEmployeeProfile> =>
    axios.get(`/api/v1/peak-employees/${id}`);

  // public updateEmployeeRoles = (
  //   id: string,
  //   requestData: Array<IEmployeePermissionSectionItem>,
  // ): Promise<any> => axios.post(`/api/v1/security/employee/${id}`, requestData);

  public createPeakEmployee = (requestData: IPeakEmployeeFormData): Promise<IPeakEmployeeProfile> =>
    axios.post(`/api/v1/peak-employees`, requestData);

  public updatePeakEmployee = (
    id: number,
    requestData: IPeakEmployeeFormData,
  ): Promise<IPeakEmployeeProfile> => axios.put(`/api/v1/peak-employees/${id}`, requestData);

  public changePeakEmployeeStatus = (
    employeeId: number,
    active: boolean,
  ): Promise<ISuccessResponse> =>
    axios.patch(`/api/v1/peak-employees/${employeeId}/change-status`, { active });
}
